import React, { Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CountryLabel from "./CountryLabel";
import { updateSettings } from "../../actions/user.action";
import { useTranslation } from "react-i18next";

import countries from "../../constants/countries";
import { adjustGridRowAndCol, checkCorrectNeighbour } from "../../utils";
import RippleButton from "../buttons/RippleButton";

import { motion, useAnimation } from "framer-motion";

const SingleCountry = ({ header, Alpha2Codes, completed, labels }) => {
  const { t } = useTranslation();

  const { settings } = useSelector(({ user }) => user);
  const { guesses } = useSelector(({ game }) => game.neighbours);
  const dispatch = useDispatch();

  const controls = useAnimation();
  useEffect(() => {
    if (!settings.noImage) {
      controls.start({ scale: 1 });
    } else {
      controls.start({ scale: 0 });
    }
  }, [settings.noImage, controls]);

  const countryNames = labels
    ? [...labels]
    : Alpha2Codes.map(
        (Alpha2Code) =>
          countries.filter(
            (item) => item?.Alpha2Code === Alpha2Code?.toUpperCase(),
          )[0]?.name,
      );

  const isCorrectNeighbours = Alpha2Codes.map((Alpha2Code) =>
    checkCorrectNeighbour(
      Alpha2Code,
      guesses.map(({ Alpha2Code }) => Alpha2Code.toUpperCase()),
    ),
  );

  const gridColumns = [
    "",
    "grid-cols-1",
    "grid-cols-2",
    "grid-cols-3",
    "grid-cols-4",
    "grid-cols-5",
    "grid-cols-6",
    "grid-cols-7",
    "grid-cols-8",
    "grid-cols-9",
    "grid-cols-10",
    "grid-cols-11",
    "grid-cols-12",
  ][adjustGridRowAndCol(Alpha2Codes.length)];

  return (
    <>
      <span
        className={`relative mt-6 w-full text-center font-bold text-slate-800 dark:text-slate-100 `}
      >
        {t(header)}
      </span>
      <div className={`my-4 grid w-full gap-4 ${gridColumns}`}>
        {Alpha2Codes.sort((a, b) =>
          settings.sortNeighboursAlphabetically
            ? a.name?.localeCompare(b.name)
            : 1,
        ).map((Alpha2Code, idx) => (
          <motion.div
            key={`svg-map-${idx}`}
            className="relative"
            initial={{ opacity: 0, translateX: 20, translateY: 20 }}
            animate={{ opacity: 1, translateX: 0, translateY: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            {(completed || isCorrectNeighbours[idx]) && (
              <CountryLabel label={countryNames[idx]} />
            )}
            {!settings.noImage || completed ? (
              <motion.div
                initial={{ scale: 0.8 }}
                animate={controls}
                transition={{ delay: 0.25 * idx }}
                className={`min-h-68 relative flex w-full transform overflow-hidden rounded-lg p-4 transition-transform duration-500 ${
                  isCorrectNeighbours[idx]
                    ? "bg-pink-500 invert dark:bg-majky-500 dark:invert-0"
                    : "border-2 border-solid border-slate-500 "
                }`}
              >
                <Suspense fallback={null}>
                  <img
                    className="pointer-events-all m-auto h-full select-none transition-all duration-700 ease-in dark:invert"
                    style={{
                      transform: `rotate(${
                        settings.rotationMode
                          ? Math.floor(Math.random() * 360)
                          : 0
                      }deg)`,
                    }}
                    alt={"country to guess"}
                    height="208"
                    width="208"
                    src={`/data/${Alpha2Code?.toLowerCase()}/map.svg`}
                  />
                </Suspense>
              </motion.div>
            ) : (
              <div className="relative mt-4 flex min-h-20 w-full max-w-lg flex-col items-center justify-center">
                <RippleButton
                  className="z-4 absolute my-2 w-3/4 rounded border-2 p-1 py-4 font-bold uppercase hover:bg-majky-50 active:bg-white dark:hover:bg-majky-800 dark:active:bg-slate-700"
                  onClick={() => {
                    dispatch(updateSettings({ ...settings, noImage: false }));
                  }}
                >
                  <img
                    alt="🗺️"
                    src="/images/1f5fa.png"
                    className="mr-1 inline-block h-6 w-6"
                  />
                  {t("Show map!")}
                </RippleButton>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </>
  );
};

export default SingleCountry;
